import { useMutation, useQuery } from '@apollo/client';
import {
  Card,
  Stack,
  Toggle,
  Box,
  ButtonLink,
  Heading,
  Text,
  Strong,
  PageBlock,
  Inline,
  Divider,
  Page,
  Hidden,
} from 'braid-design-system';
import queryString from 'query-string';
import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import CustomLoader from 'src/components/CustomLoader/CustomLoader';
import Header from 'src/components/Header/Header';
import Redirect from 'src/components/Redirect/Redirect';
import { SiteWideFooter } from 'src/components/SiteWideFooter';
import { getConfigValues } from 'src/config';
import Routes from 'src/config/routes';
import { useCommand } from 'src/hooks/command';
import BUYER_DETAILS from 'src/queries/buyerDetails';
import { subscribeUser, unsubscribeUser } from 'src/services/api';
import { tracking } from 'src/tracking';
import type { Buyer } from 'src/types/buyer';

import BusinessType from './components/BusinessType/BusinessType';
import ElementOverlay from './components/ElementOverlay/ElementOverlay';
import Industries from './components/Industries/Industries';
import Keywords from './components/Keywords/Keywords';
import PreferredLocation from './components/PreferredLocation/PreferredLocation';
import PriceRange from './components/PriceRange/PriceRange';
import POST_DRIP_EVENT from './queries/postDripEvent';

// --- Static copy ---
const COPY_DEFAULT = {
  title: 'Manage your preferences',
  message: (
    <>
      <Text data={{ testid: 'message' }}>
        You can manage the type of business recommendations we email you by
        updating your preferences below.
      </Text>
      <Text>
        Simply complete your preferences and we&apos;ll do the rest. The more
        specific you can be, the better the match. It&apos;s that simple!
      </Text>
    </>
  ),
};
const COPY_RELEVANT = {
  title: 'Thanks for your feedback!',
  message: (
    <Text data={{ testid: 'message' }}>
      We&apos;ll continue to send you recommendations based on your preferences
      below. You can update or add to them at any time{' '}
      <Strong>via the link in your profile</Strong>. This will ensure you only
      get the most relevant businesses sent to you.
    </Text>
  ),
};

const Preferences = () => {
  const initialRender = useRef(true);
  const [sendEmails, setSendEmails] = useState(true);

  const { data, refetch, loading, error } = useQuery<Buyer>(BUYER_DETAILS);
  const userId = data?.buyer.userId as string;
  const { industries, districts, businessTypes, price, excludePoa, keywords } =
    data?.buyer?.recommendationPreferences ?? {
      industries: [],
      districts: [],
      businessTypes: [],
      keywords: [],
    };
  const subscriptionStatus = data?.buyer?.subscription?.subscriptionStatus;
  const { command: unsubscribe } = useCommand(unsubscribeUser);
  const urlLocation = useLocation();
  const { recommendation, listingId, source } = queryString.parseUrl(
    urlLocation.search,
  ).query;
  const [postEvent, { called }] = useMutation(POST_DRIP_EVENT);

  useEffect(() => {
    // If the user has come from the buyer engagement email, send a drip event
    if (source === 'be0822' && !called) {
      postEvent({
        variables: {
          input: {
            action: 'engagement campaign aug 22 clickthrough',
          },
        },
      });
    }
  });

  useEffect(() => {
    if (subscriptionStatus === 'Subscribed') {
      setSendEmails(true);
    } else {
      setSendEmails(false);
    }
  }, [subscriptionStatus]);

  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
      if (recommendation) {
        const buyer = !loading ? data?.buyer : null;
        const context = {
          buyerId: String(buyer?.userId),
          listingId: String(listingId),
        };
        if (recommendation === 'relevant') {
          tracking.preferences.recommendationFeedback.relevant(context);
        } else {
          tracking.preferences.recommendationFeedback.irrelevant(context);
        }
      }
    } else if (userId) {
      if (sendEmails) {
        subscribeUser(userId);
      } else {
        unsubscribe(userId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sendEmails]);

  // Handle GraphQL auth errors by redirecting to sign-in
  if (error) {
    const authErrors = ['UNAUTHENTICATED', 'FORBIDDEN'];
    const isAuthError = error.graphQLErrors.some(
      (err) =>
        typeof err.extensions?.code === 'string' &&
        authErrors.includes(err.extensions.code),
    );

    if (isAuthError) {
      const redirectUrl = queryString.stringifyUrl({
        url: `${getConfigValues().HEADER_SERVER_BASE}${Routes.SIGN_IN_URL}`,
        query: {
          ReturnUrl: urlLocation.pathname + urlLocation.search,
        },
      });
      return <Redirect path={redirectUrl} />;
    }
  }

  // Copy selection
  const pageCopy = recommendation === 'relevant' ? COPY_RELEVANT : COPY_DEFAULT;

  return (
    <Page footer={<SiteWideFooter />} footerPosition="belowFold">
      <Header title="Customise your recommendations" />

      <Hidden below="tablet">
        <Divider />
      </Hidden>

      <Box paddingTop="xlarge">
        <PageBlock width="small">
          <Stack space="xxlarge">
            <Stack space="medium">
              <Heading level="2" component="h1">
                {pageCopy.title}
              </Heading>
              {pageCopy.message}
            </Stack>
            <Stack space="medium">
              {!loading && (
                <Toggle
                  id="emailSubscriptionToggle"
                  onChange={() => setSendEmails(!sendEmails)}
                  label="Send me email recommendations"
                  on={sendEmails}
                />
              )}
              {loading ? (
                <CustomLoader />
              ) : (
                <ElementOverlay visible={!sendEmails}>
                  <Card>
                    <Stack space="xlarge">
                      <Keywords keywords={keywords} refetch={refetch} />
                      <Industries industries={industries} refetch={refetch} />
                      <PreferredLocation
                        districts={districts}
                        refetch={refetch}
                      />
                      <PriceRange
                        price={price}
                        excludePoa={Boolean(excludePoa)}
                      />
                      <BusinessType businessTypes={businessTypes} />
                      <Inline space="none">
                        <ButtonLink
                          href="/buyer/preferences/submitted"
                          tone="formAccent"
                        >
                          Done
                        </ButtonLink>
                      </Inline>
                    </Stack>
                  </Card>
                </ElementOverlay>
              )}
            </Stack>
          </Stack>
        </PageBlock>
      </Box>
    </Page>
  );
};

export default Preferences;
